import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ─── EmailJS config ───────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = "service_coa0au5";
const EMAILJS_TEMPLATE_ID = "template_ftbsfti";
const EMAILJS_PUBLIC_KEY  = "gzle2jMk9oyKll3Qv";

const LANGUAGES = [
  { code: "en-us", name: "English US" },
  { code: "en-uk", name: "English UK" },
  { code: "es", name: "Español" },
  { code: "fr", name: "Français" },
  { code: "pt", name: "Português" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "ru", name: "Русский" },
  { code: "zh", name: "中文" },
  { code: "pl", name: "Polski" },
  { code: "ja", name: "日本語" },
];

const T = {
  es: {
    title: "Traductor de Patrones de Crochet",
    subtitle: "Traduce tus patrones preservando cada punto, abreviación y salto de línea",
    originalPattern: "Patrón Original",
    translatedPattern: "Patrón Traducido",
    translateTo: "Traducir a:",
    translateBtn: "Traducir texto",
    translating: "Traduciendo...",
    uploadPdf: "Subir PDF",
    loadingPdf: "Cargando...",
    clean: "Limpiar",
    copy: "Copiar",
    copied: "Copiado 📋",
    save: "Guardar",
    history: "Historial",
    noHistory: "No hay patrones guardados aún",
    explainTitle: "Explicar una Fila / Vuelta",
    explainSubtitle: "Pega una fila y obtén una explicación para principiantes",
    explainBtn: "Explicar",
    explaining: "Explicando...",
    explanationLabel: "Explicación:",
    pdfOptions: "Opciones de PDF",
    outputType: "Tipo de salida:",
    outputPdf: "PDF (con imágenes y layout)",
    outputText: "Solo texto",
    pagesLabel: "Páginas a traducir:",
    downloadPdf: "Descargar PDF traducido",
    translateToText: "Traducir a texto",
    processing: "Procesando...",
    removePdf: "Quitar PDF",
    pagesHint: 'Escribe "all" para todas las páginas, o ej: "1,3,5" o "1-5,8-10"',
    saveModal: "Guardar Patrón",
    saveLabel: "Nombre del patrón:",
    savePlaceholder: "ej: Bufanda Navideña",
    cancel: "Cancelar",
    saveBtn: "Guardar",
    pasteHere: "Pega tu patrón aquí...\n\nEjemplo:\nVuelta 1: 6 pb en anillo mágico (6)\nVuelta 2: 2 pb en cada p (12)",
    appearsHere: "Tu patrón traducido aparecerá aquí...",
    rowPlaceholder: "Ejemplo: Vuelta 3: *1 pb, 2 pb en sig p* repetir 6 veces (18 pb)",
    footer: "Las abreviaciones y el formato se preservan exactamente como están escritos 🧶",
    toastTranslated: "¡Patrón traducido! 🧶",
    toastExplained: "¡Fila explicada! 💡",
    toastSaved: "Guardado 💾",
    toastLoaded: "Cargado:",
    toastDeleted: "Eliminado",
    toastPdfLoaded: "PDF cargado:",
    toastPdfDone: "¡PDF traducido descargado! 🎉",
    toastPdfText: "¡PDF traducido a texto! 📝",
    toastPdfHtml: "¡HTML traducido descargado! 🌐",
    toastPdfClean: "¡PDF limpio descargado! 📄",
    outputClean: "PDF limpio (texto + imágenes, tipografía consistente)",
    downloadClean: "Descargar PDF limpio",
    outputHtml: "HTML (texto + imágenes, ideal para imprimir)",
    downloadHtml: "Descargar HTML traducido",
    errPaste: "Pega un patrón primero",
    errRow: "Pega una fila primero",
    errName: "Ingresa un nombre",
    errPdf: "Sube un PDF primero",
    errOnlyPdf: "Solo se aceptan archivos PDF",
    errTranslate: "Error al traducir.",
    errExplain: "Error al explicar.",
    errPdfTranslate: "Error al traducir el PDF.",
    errTimeout: "Tiempo de espera agotado. Intenta con menos páginas.",
    pages: "páginas",
    total: "Total:",
    reportBtn: "Reportar error",
    reportTitle: "Reportar un Error",
    reportSubtitle: "¿Algo no funcionó bien? Cuéntanos y lo arreglamos.",
    reportFile: "Archivo que estabas traduciendo:",
    reportDesc: "Descripción del error:",
    reportDescPlaceholder: "Describe qué pasó, qué esperabas ver y qué viste...",
    reportScreenshot: "Captura de pantalla (opcional):",
    reportSend: "Enviar reporte",
    reportSending: "Enviando...",
    reportSuccess: "¡Reporte enviado! Gracias 🙏",
    reportError: "Error al enviar. Inténtalo de nuevo.",
    reportDescRequired: "Escribe una descripción del error.",
  },
  en: {
    title: "Crochet Pattern Translator",
    subtitle: "Translate your patterns preserving every stitch, abbreviation and line break",
    originalPattern: "Original Pattern",
    translatedPattern: "Translated Pattern",
    translateTo: "Translate to:",
    translateBtn: "Translate text",
    translating: "Translating...",
    uploadPdf: "Upload PDF",
    loadingPdf: "Loading...",
    clean: "Clear",
    copy: "Copy",
    copied: "Copied 📋",
    save: "Save",
    history: "History",
    noHistory: "No saved patterns yet",
    explainTitle: "Explain a Row / Round",
    explainSubtitle: "Paste a row and get a beginner-friendly explanation",
    explainBtn: "Explain",
    explaining: "Explaining...",
    explanationLabel: "Explanation:",
    pdfOptions: "PDF Options",
    outputType: "Output type:",
    outputPdf: "PDF (with images and layout)",
    outputText: "Text only",
    pagesLabel: "Pages to translate:",
    downloadPdf: "Download translated PDF",
    translateToText: "Translate to text",
    processing: "Processing...",
    removePdf: "Remove PDF",
    pagesHint: 'Type "all" for all pages, or e.g. "1,3,5" or "1-5,8-10"',
    saveModal: "Save Pattern",
    saveLabel: "Pattern name:",
    savePlaceholder: "e.g. Christmas Scarf",
    cancel: "Cancel",
    saveBtn: "Save",
    pasteHere: "Paste your pattern here...\n\nExample:\nRound 1: 6 sc in magic ring (6)\nRound 2: 2 sc in each st (12)",
    appearsHere: "Your translated pattern will appear here...",
    rowPlaceholder: "Example: Round 3: *1 sc, 2 sc in next st* repeat 6 times (18 sc)",
    footer: "Abbreviations and formatting are preserved exactly as written 🧶",
    toastTranslated: "Pattern translated! 🧶",
    toastExplained: "Row explained! 💡",
    toastSaved: "Saved 💾",
    toastLoaded: "Loaded:",
    toastDeleted: "Deleted",
    toastPdfLoaded: "PDF loaded:",
    toastPdfDone: "Translated PDF downloaded! 🎉",
    toastPdfText: "PDF translated to text! 📝",
    toastPdfHtml: "Translated HTML downloaded! 🌐",
    toastPdfClean: "Clean PDF downloaded! 📄",
    outputClean: "Clean PDF (text + images, consistent typography)",
    downloadClean: "Download clean PDF",
    outputHtml: "HTML (text + images, ideal for printing)",
    downloadHtml: "Download translated HTML",
    errPaste: "Paste a pattern first",
    errRow: "Paste a row first",
    errName: "Enter a name",
    errPdf: "Upload a PDF first",
    errOnlyPdf: "Only PDF files are accepted",
    errTranslate: "Translation error.",
    errExplain: "Explanation error.",
    errPdfTranslate: "Error translating PDF.",
    errTimeout: "Timeout. Try with fewer pages.",
    pages: "pages",
    total: "Total:",
    reportBtn: "Report a bug",
    reportTitle: "Report a Bug",
    reportSubtitle: "Something didn't work? Tell us and we'll fix it.",
    reportFile: "File you were translating:",
    reportDesc: "Error description:",
    reportDescPlaceholder: "Describe what happened, what you expected and what you saw...",
    reportScreenshot: "Screenshot (optional):",
    reportSend: "Send report",
    reportSending: "Sending...",
    reportSuccess: "Report sent! Thank you 🙏",
    reportError: "Error sending. Please try again.",
    reportDescRequired: "Please describe the error.",
  },
  it: {
    title: "Traduttore di Schemi di Uncinetto",
    subtitle: "Traduci i tuoi schemi preservando ogni punto, abbreviazione e interruzione di riga",
    originalPattern: "Schema Originale",
    translatedPattern: "Schema Tradotto",
    translateTo: "Traduci in:",
    translateBtn: "Traduci testo",
    translating: "Traducendo...",
    uploadPdf: "Carica PDF",
    loadingPdf: "Caricando...",
    clean: "Pulisci",
    copy: "Copia",
    copied: "Copiato 📋",
    save: "Salva",
    history: "Cronologia",
    noHistory: "Nessuno schema salvato",
    explainTitle: "Spiega una Riga / Giro",
    explainSubtitle: "Incolla una riga e ottieni una spiegazione per principianti",
    explainBtn: "Spiega",
    explaining: "Spiegando...",
    explanationLabel: "Spiegazione:",
    pdfOptions: "Opzioni PDF",
    outputType: "Tipo di output:",
    outputPdf: "PDF (con immagini e layout)",
    outputText: "Solo testo",
    pagesLabel: "Pagine da tradurre:",
    downloadPdf: "Scarica PDF tradotto",
    translateToText: "Traduci in testo",
    processing: "Elaborando...",
    removePdf: "Rimuovi PDF",
    pagesHint: 'Scrivi "all" per tutte le pagine, es: "1,3,5" o "1-5,8-10"',
    saveModal: "Salva Schema",
    saveLabel: "Nome dello schema:",
    savePlaceholder: "es: Sciarpa di Natale",
    cancel: "Annulla",
    saveBtn: "Salva",
    pasteHere: "Incolla il tuo schema qui...\n\nEsempio:\nGiro 1: 6 mb in anello magico (6)\nGiro 2: 2 mb in ogni p (12)",
    appearsHere: "Il tuo schema tradotto apparirà qui...",
    rowPlaceholder: "Esempio: Giro 3: *1 mb, 2 mb nel p successivo* ripetere 6 volte (18 mb)",
    footer: "Le abbreviazioni e la formattazione vengono preservate esattamente come scritte 🧶",
    toastTranslated: "Schema tradotto! 🧶",
    toastExplained: "Riga spiegata! 💡",
    toastSaved: "Salvato 💾",
    toastLoaded: "Caricato:",
    toastDeleted: "Eliminato",
    toastPdfLoaded: "PDF caricato:",
    toastPdfDone: "PDF tradotto scaricato! 🎉",
    toastPdfText: "PDF tradotto in testo! 📝",
    toastPdfHtml: "HTML tradotto scaricato! 🌐",
    toastPdfClean: "PDF pulito scaricato! 📄",
    outputClean: "PDF pulito (testo + immagini, tipografia consistente)",
    downloadClean: "Scarica PDF pulito",
    outputHtml: "HTML (testo + immagini, ideale per stampare)",
    downloadHtml: "Scarica HTML tradotto",
    errPaste: "Incolla prima uno schema",
    errRow: "Incolla prima una riga",
    errName: "Inserisci un nome",
    errPdf: "Carica prima un PDF",
    errOnlyPdf: "Sono accettati solo file PDF",
    errTranslate: "Errore di traduzione.",
    errExplain: "Errore nella spiegazione.",
    errPdfTranslate: "Errore nella traduzione del PDF.",
    errTimeout: "Timeout. Prova con meno pagine.",
    pages: "pagine",
    total: "Totale:",
    reportBtn: "Segnala errore",
    reportTitle: "Segnala un Errore",
    reportSubtitle: "Qualcosa non ha funzionato? Diccelo e lo risolviamo.",
    reportFile: "File che stavi traducendo:",
    reportDesc: "Descrizione dell'errore:",
    reportDescPlaceholder: "Descrivi cosa è successo, cosa ti aspettavi e cosa hai visto...",
    reportScreenshot: "Screenshot (opzionale):",
    reportSend: "Invia segnalazione",
    reportSending: "Invio...",
    reportSuccess: "Segnalazione inviata! Grazie 🙏",
    reportError: "Errore nell'invio. Riprova.",
    reportDescRequired: "Scrivi una descrizione dell'errore.",
  },
  fr: {
    title: "Traducteur de Patrons de Crochet",
    subtitle: "Traduisez vos patrons en préservant chaque point, abréviation et saut de ligne",
    originalPattern: "Patron Original",
    translatedPattern: "Patron Traduit",
    translateTo: "Traduire en :",
    translateBtn: "Traduire le texte",
    translating: "Traduction...",
    uploadPdf: "Charger PDF",
    loadingPdf: "Chargement...",
    clean: "Effacer",
    copy: "Copier",
    copied: "Copié 📋",
    save: "Sauvegarder",
    history: "Historique",
    noHistory: "Aucun patron sauvegardé",
    explainTitle: "Expliquer une Rangée / Tour",
    explainSubtitle: "Collez une rangée et obtenez une explication pour débutants",
    explainBtn: "Expliquer",
    explaining: "Explication...",
    explanationLabel: "Explication :",
    pdfOptions: "Options PDF",
    outputType: "Type de sortie :",
    outputPdf: "PDF (avec images et mise en page)",
    outputText: "Texte uniquement",
    pagesLabel: "Pages à traduire :",
    downloadPdf: "Télécharger le PDF traduit",
    translateToText: "Traduire en texte",
    processing: "Traitement...",
    removePdf: "Retirer le PDF",
    pagesHint: 'Tapez "all" pour toutes les pages, ex : "1,3,5" ou "1-5,8-10"',
    saveModal: "Sauvegarder le Patron",
    saveLabel: "Nom du patron :",
    savePlaceholder: "ex : Écharpe de Noël",
    cancel: "Annuler",
    saveBtn: "Sauvegarder",
    pasteHere: "Collez votre patron ici...\n\nExemple :\nTour 1 : 6 ms en anneau magique (6)\nTour 2 : 2 ms dans chaque m (12)",
    appearsHere: "Votre patron traduit apparaîtra ici...",
    rowPlaceholder: "Exemple : Tour 3 : *1 ms, 2 ms dans la m suivante* répéter 6 fois (18 ms)",
    footer: "Les abréviations et la mise en forme sont préservées exactement telles qu'elles sont écrites 🧶",
    toastTranslated: "Patron traduit ! 🧶",
    toastExplained: "Rangée expliquée ! 💡",
    toastSaved: "Sauvegardé 💾",
    toastLoaded: "Chargé :",
    toastDeleted: "Supprimé",
    toastPdfLoaded: "PDF chargé :",
    toastPdfDone: "PDF traduit téléchargé ! 🎉",
    toastPdfText: "PDF traduit en texte ! 📝",
    toastPdfHtml: "HTML traduit téléchargé ! 🌐",
    toastPdfClean: "PDF propre téléchargé ! 📄",
    outputClean: "PDF propre (texte + images, typographie cohérente)",
    downloadClean: "Télécharger le PDF propre",
    outputHtml: "HTML (texte + images, idéal pour imprimer)",
    downloadHtml: "Télécharger le HTML traduit",
    errPaste: "Collez d'abord un patron",
    errRow: "Collez d'abord une rangée",
    errName: "Entrez un nom",
    errPdf: "Chargez d'abord un PDF",
    errOnlyPdf: "Seuls les fichiers PDF sont acceptés",
    errTranslate: "Erreur de traduction.",
    errExplain: "Erreur d'explication.",
    errPdfTranslate: "Erreur lors de la traduction du PDF.",
    errTimeout: "Délai dépassé. Essayez avec moins de pages.",
    pages: "pages",
    total: "Total :",
    reportBtn: "Signaler une erreur",
    reportTitle: "Signaler une Erreur",
    reportSubtitle: "Quelque chose n'a pas fonctionné ? Dites-le nous.",
    reportFile: "Fichier que vous traduisiez :",
    reportDesc: "Description de l'erreur :",
    reportDescPlaceholder: "Décrivez ce qui s'est passé, ce que vous attendiez et ce que vous avez vu...",
    reportScreenshot: "Capture d'écran (optionnel) :",
    reportSend: "Envoyer le rapport",
    reportSending: "Envoi...",
    reportSuccess: "Rapport envoyé ! Merci 🙏",
    reportError: "Erreur d'envoi. Réessayez.",
    reportDescRequired: "Veuillez décrire l'erreur.",
  },
  pt: {
    title: "Tradutor de Padrões de Crochê",
    subtitle: "Traduza seus padrões preservando cada ponto, abreviação e quebra de linha",
    originalPattern: "Padrão Original",
    translatedPattern: "Padrão Traduzido",
    translateTo: "Traduzir para:",
    translateBtn: "Traduzir texto",
    translating: "Traduzindo...",
    uploadPdf: "Carregar PDF",
    loadingPdf: "Carregando...",
    clean: "Limpar",
    copy: "Copiar",
    copied: "Copiado 📋",
    save: "Guardar",
    history: "Histórico",
    noHistory: "Nenhum padrão guardado ainda",
    explainTitle: "Explicar uma Fileira / Volta",
    explainSubtitle: "Cole uma fileira e obtenha uma explicação para iniciantes",
    explainBtn: "Explicar",
    explaining: "Explicando...",
    explanationLabel: "Explicação:",
    pdfOptions: "Opções de PDF",
    outputType: "Tipo de saída:",
    outputPdf: "PDF (com imagens e layout)",
    outputText: "Apenas texto",
    pagesLabel: "Páginas a traduzir:",
    downloadPdf: "Baixar PDF traduzido",
    translateToText: "Traduzir para texto",
    processing: "Processando...",
    removePdf: "Remover PDF",
    pagesHint: 'Digite "all" para todas as páginas, ex: "1,3,5" ou "1-5,8-10"',
    saveModal: "Guardar Padrão",
    saveLabel: "Nome do padrão:",
    savePlaceholder: "ex: Cachecol de Natal",
    cancel: "Cancelar",
    saveBtn: "Guardar",
    pasteHere: "Cole seu padrão aqui...\n\nExemplo:\nVolta 1: 6 pb no anel mágico (6)\nVolta 2: 2 pb em cada p (12)",
    appearsHere: "Seu padrão traduzido aparecerá aqui...",
    rowPlaceholder: "Exemplo: Volta 3: *1 pb, 2 pb no próximo p* repetir 6 vezes (18 pb)",
    footer: "As abreviações e a formatação são preservadas exatamente como estão escritas 🧶",
    toastTranslated: "Padrão traduzido! 🧶",
    toastExplained: "Fileira explicada! 💡",
    toastSaved: "Guardado 💾",
    toastLoaded: "Carregado:",
    toastDeleted: "Eliminado",
    toastPdfLoaded: "PDF carregado:",
    toastPdfDone: "PDF traduzido baixado! 🎉",
    toastPdfText: "PDF traduzido para texto! 📝",
    toastPdfHtml: "HTML traduzido baixado! 🌐",
    toastPdfClean: "PDF limpo baixado! 📄",
    outputClean: "PDF limpo (texto + imagens, tipografia consistente)",
    downloadClean: "Baixar PDF limpo",
    outputHtml: "HTML (texto + imagens, ideal para imprimir)",
    downloadHtml: "Baixar HTML traduzido",
    errPaste: "Cole um padrão primeiro",
    errRow: "Cole uma fileira primeiro",
    errName: "Insira um nome",
    errPdf: "Carregue um PDF primeiro",
    errOnlyPdf: "Apenas arquivos PDF são aceitos",
    errTranslate: "Erro ao traduzir.",
    errExplain: "Erro ao explicar.",
    errPdfTranslate: "Erro ao traduzir o PDF.",
    errTimeout: "Tempo esgotado. Tente com menos páginas.",
    pages: "páginas",
    total: "Total:",
    reportBtn: "Reportar erro",
    reportTitle: "Reportar um Erro",
    reportSubtitle: "Algo não funcionou? Conte-nos e vamos corrigir.",
    reportFile: "Arquivo que estava traduzindo:",
    reportDesc: "Descrição do erro:",
    reportDescPlaceholder: "Descreva o que aconteceu, o que esperava e o que viu...",
    reportScreenshot: "Captura de tela (opcional):",
    reportSend: "Enviar relatório",
    reportSending: "Enviando...",
    reportSuccess: "Relatório enviado! Obrigado 🙏",
    reportError: "Erro ao enviar. Tente novamente.",
    reportDescRequired: "Escreva uma descrição do erro.",
  },
  de: {
    title: "Häkelmuster-Übersetzer",
    subtitle: "Übersetzen Sie Ihre Muster und bewahren Sie dabei jede Abkürzung und jeden Zeilenumbruch",
    originalPattern: "Originalmuster",
    translatedPattern: "Übersetztes Muster",
    translateTo: "Übersetzen in:",
    translateBtn: "Text übersetzen",
    translating: "Übersetze...",
    uploadPdf: "PDF hochladen",
    loadingPdf: "Laden...",
    clean: "Löschen",
    copy: "Kopieren",
    copied: "Kopiert 📋",
    save: "Speichern",
    history: "Verlauf",
    noHistory: "Noch keine gespeicherten Muster",
    explainTitle: "Reihe / Runde erklären",
    explainSubtitle: "Fügen Sie eine Reihe ein und erhalten Sie eine Erklärung für Anfänger",
    explainBtn: "Erklären",
    explaining: "Erkläre...",
    explanationLabel: "Erklärung:",
    pdfOptions: "PDF-Optionen",
    outputType: "Ausgabetyp:",
    outputPdf: "PDF (mit Bildern und Layout)",
    outputText: "Nur Text",
    pagesLabel: "Zu übersetzende Seiten:",
    downloadPdf: "Übersetztes PDF herunterladen",
    translateToText: "In Text übersetzen",
    processing: "Verarbeite...",
    removePdf: "PDF entfernen",
    pagesHint: 'Geben Sie "all" für alle Seiten ein, z.B. "1,3,5" oder "1-5,8-10"',
    saveModal: "Muster speichern",
    saveLabel: "Mustername:",
    savePlaceholder: "z.B. Weihnachtsschal",
    cancel: "Abbrechen",
    saveBtn: "Speichern",
    pasteHere: "Fügen Sie Ihr Muster hier ein...\n\nBeispiel:\nRunde 1: 6 fm in Zauberring (6)\nRunde 2: 2 fm in jede M (12)",
    appearsHere: "Ihr übersetztes Muster erscheint hier...",
    rowPlaceholder: "Beispiel: Runde 3: *1 fm, 2 fm in nächste M* 6-mal wiederholen (18 fm)",
    footer: "Abkürzungen und Formatierung werden genau so beibehalten, wie sie geschrieben sind 🧶",
    toastTranslated: "Muster übersetzt! 🧶",
    toastExplained: "Reihe erklärt! 💡",
    toastSaved: "Gespeichert 💾",
    toastLoaded: "Geladen:",
    toastDeleted: "Gelöscht",
    toastPdfLoaded: "PDF geladen:",
    toastPdfDone: "Übersetztes PDF heruntergeladen! 🎉",
    toastPdfText: "PDF in Text übersetzt! 📝",
    toastPdfHtml: "Übersetztes HTML heruntergeladen! 🌐",
    toastPdfClean: "Sauberes PDF heruntergeladen! 📄",
    outputClean: "Sauberes PDF (Text + Bilder, einheitliche Schrift)",
    downloadClean: "Sauberes PDF herunterladen",
    outputHtml: "HTML (Text + Bilder, ideal zum Drucken)",
    downloadHtml: "Übersetztes HTML herunterladen",
    errPaste: "Fügen Sie zuerst ein Muster ein",
    errRow: "Fügen Sie zuerst eine Reihe ein",
    errName: "Geben Sie einen Namen ein",
    errPdf: "Laden Sie zuerst ein PDF hoch",
    errOnlyPdf: "Nur PDF-Dateien werden akzeptiert",
    errTranslate: "Übersetzungsfehler.",
    errExplain: "Erklärungsfehler.",
    errPdfTranslate: "Fehler beim Übersetzen des PDFs.",
    errTimeout: "Zeitüberschreitung. Versuchen Sie es mit weniger Seiten.",
    pages: "Seiten",
    total: "Gesamt:",
    reportBtn: "Fehler melden",
    reportTitle: "Fehler Melden",
    reportSubtitle: "Etwas hat nicht funktioniert? Sagen Sie es uns.",
    reportFile: "Datei, die Sie übersetzt haben:",
    reportDesc: "Fehlerbeschreibung:",
    reportDescPlaceholder: "Beschreiben Sie, was passiert ist...",
    reportScreenshot: "Screenshot (optional):",
    reportSend: "Bericht senden",
    reportSending: "Senden...",
    reportSuccess: "Bericht gesendet! Danke 🙏",
    reportError: "Sendefehler. Bitte erneut versuchen.",
    reportDescRequired: "Bitte beschreiben Sie den Fehler.",
  },
  ru: {
    title: "Переводчик Схем Вязания",
    subtitle: "Переводите схемы, сохраняя каждое сокращение, стежок и перенос строки",
    originalPattern: "Оригинальная Схема",
    translatedPattern: "Переведённая Схема",
    translateTo: "Перевести на:",
    translateBtn: "Перевести текст",
    translating: "Перевод...",
    uploadPdf: "Загрузить PDF",
    loadingPdf: "Загрузка...",
    clean: "Очистить",
    copy: "Копировать",
    copied: "Скопировано 📋",
    save: "Сохранить",
    history: "История",
    noHistory: "Нет сохранённых схем",
    explainTitle: "Объяснить Ряд / Круг",
    explainSubtitle: "Вставьте ряд и получите объяснение для начинающих",
    explainBtn: "Объяснить",
    explaining: "Объясняю...",
    explanationLabel: "Объяснение:",
    pdfOptions: "Параметры PDF",
    outputType: "Тип вывода:",
    outputPdf: "PDF (с изображениями и макетом)",
    outputText: "Только текст",
    pagesLabel: "Страницы для перевода:",
    downloadPdf: "Скачать переведённый PDF",
    translateToText: "Перевести в текст",
    processing: "Обработка...",
    removePdf: "Убрать PDF",
    pagesHint: 'Введите "all" для всех страниц, например: "1,3,5" или "1-5,8-10"',
    saveModal: "Сохранить Схему",
    saveLabel: "Название схемы:",
    savePlaceholder: "напр: Новогодний шарф",
    cancel: "Отмена",
    saveBtn: "Сохранить",
    pasteHere: "Вставьте схему сюда...\n\nПример:\nКруг 1: 6 сбн в магическое кольцо (6)\nКруг 2: 2 сбн в каждую п (12)",
    appearsHere: "Ваша переведённая схема появится здесь...",
    rowPlaceholder: "Пример: Круг 3: *1 сбн, 2 сбн в сл п* повторить 6 раз (18 сбн)",
    footer: "Сокращения и форматирование сохраняются точно так, как написано 🧶",
    toastTranslated: "Схема переведена! 🧶",
    toastExplained: "Ряд объяснён! 💡",
    toastSaved: "Сохранено 💾",
    toastLoaded: "Загружено:",
    toastDeleted: "Удалено",
    toastPdfLoaded: "PDF загружен:",
    toastPdfDone: "Переведённый PDF скачан! 🎉",
    toastPdfText: "PDF переведён в текст! 📝",
    toastPdfHtml: "Переведённый HTML скачан! 🌐",
    toastPdfClean: "Чистый PDF скачан! 📄",
    outputClean: "Чистый PDF (текст + изображения, единый шрифт)",
    downloadClean: "Скачать чистый PDF",
    outputHtml: "HTML (текст + изображения, идеально для печати)",
    downloadHtml: "Скачать переведённый HTML",
    errPaste: "Сначала вставьте схему",
    errRow: "Сначала вставьте ряд",
    errName: "Введите название",
    errPdf: "Сначала загрузите PDF",
    errOnlyPdf: "Принимаются только PDF файлы",
    errTranslate: "Ошибка перевода.",
    errExplain: "Ошибка объяснения.",
    errPdfTranslate: "Ошибка перевода PDF.",
    errTimeout: "Превышено время ожидания. Попробуйте меньше страниц.",
    pages: "страниц",
    total: "Всего:",
    reportBtn: "Сообщить об ошибке",
    reportTitle: "Сообщить об Ошибке",
    reportSubtitle: "Что-то не работает? Расскажите нам.",
    reportFile: "Файл, который вы переводили:",
    reportDesc: "Описание ошибки:",
    reportDescPlaceholder: "Опишите, что произошло...",
    reportScreenshot: "Скриншот (необязательно):",
    reportSend: "Отправить отчёт",
    reportSending: "Отправка...",
    reportSuccess: "Отчёт отправлен! Спасибо 🙏",
    reportError: "Ошибка отправки. Попробуйте ещё раз.",
    reportDescRequired: "Опишите ошибку.",
  },
};

const formatTime = (s) => `${Math.floor(s / 60)}m ${s % 60}s`;

// ─── Load EmailJS script dynamically ─────────────────────────────────────────
function loadEmailJS() {
  return new Promise((resolve) => {
    if (window.emailjs) { resolve(); return; }
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
    script.onload = () => { window.emailjs.init(EMAILJS_PUBLIC_KEY); resolve(); };
    document.head.appendChild(script);
  });
}

// ─── Convert image file to base64 ────────────────────────────────────────────
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function App() {
  const [uiLang, setUiLang] = useState("es");
  const t = T[uiLang];

  const [pattern, setPattern] = useState("");
  const [targetLang, setTargetLang] = useState("es");
  const [translated, setTranslated] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState("");
  const [explanation, setExplanation] = useState("");
  const [isExplaining, setIsExplaining] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [toast, setToastMsg] = useState(null);

  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPageCount, setPdfPageCount] = useState(0);
  const [selectedPages, setSelectedPages] = useState("all");
  const [outputType, setOutputType] = useState("html");
  const [isUploadingPdf, setIsUploadingPdf] = useState(false);
  const [showPdfOptions, setShowPdfOptions] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0, message: "" });
  const fileInputRef = useRef(null);
  const progressIntervalRef = useRef(null);

  // ── Bug report state ───────────────────────────────────────────────────────
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportDesc, setReportDesc] = useState("");
  const [reportScreenshot, setReportScreenshot] = useState(null);
  const [reportScreenshotName, setReportScreenshotName] = useState("");
  const [isSendingReport, setIsSendingReport] = useState(false);
  const screenshotInputRef = useRef(null);

  // ── Expand modal ───────────────────────────────────────────────────────────
  const [expandModal, setExpandModal] = useState(null); // null | { title, content, readOnly, onChange }

  const showToast = (msg, type = "success") => {
    setToastMsg({ msg, type });
    setTimeout(() => setToastMsg(null), 3500);
  };

  const startTimer = (seconds) => {
    setTimer(seconds);
    timerRef.current = setInterval(() => {
      setTimer((prev) => { if (prev <= 1) { clearInterval(timerRef.current); return 0; } return prev - 1; });
    }, 1000);
  };

  const stopTimer = () => { clearInterval(timerRef.current); setTimer(0); };

  const handleTranslate = async () => {
    if (!pattern.trim()) { showToast(t.errPaste, "error"); return; }
    setIsTranslating(true);
    startTimer(pattern.length > 2000 ? 20 : pattern.length > 500 ? 10 : 5);
    try {
      const langName = LANGUAGES.find((l) => l.code === targetLang)?.name;
      const res = await axios.post(`${API}/translate`, { pattern, target_language: langName });
      setTranslated(res.data.translated_pattern);
      showToast(t.toastTranslated);
    } catch { showToast(t.errTranslate, "error"); }
    finally { stopTimer(); setIsTranslating(false); }
  };

  const handleExplain = async () => {
    if (!selectedRow.trim()) { showToast(t.errRow, "error"); return; }
    setIsExplaining(true);
    try {
      const langName = LANGUAGES.find((l) => l.code === targetLang)?.name;
      const res = await axios.post(`${API}/explain_row`, { row_text: selectedRow, target_language: langName });
      setExplanation(res.data.explanation);
      showToast(t.toastExplained);
    } catch { showToast(t.errExplain, "error"); }
    finally { setIsExplaining(false); }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { showToast(t.errOnlyPdf, "error"); return; }
    setPdfFile(file);
    setIsUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/pdf_info`, formData);
      setPdfPageCount(res.data.page_count);
      setShowPdfOptions(true);
      setSelectedPages("all");
      showToast(`${t.toastPdfLoaded} ${res.data.page_count} ${t.pages} 📄`);
    } catch {
      setPdfPageCount(0);
      setShowPdfOptions(true);
      showToast(`${t.toastPdfLoaded} PDF`);
    } finally {
      setIsUploadingPdf(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleTranslatePdf = async () => {
    if (!pdfFile) { showToast(t.errPdf, "error"); return; }
    setIsTranslating(true);
    const langName = LANGUAGES.find((l) => l.code === targetLang)?.name;
    const pagesCount = selectedPages.toLowerCase() === "all" ? (pdfPageCount || 1) : selectedPages.split(",").length;
    const estTime = outputType === "text" ? pagesCount * 15 : outputType === "html" ? pagesCount * 20 : pagesCount * 45;
    startTimer(Math.max(10, estTime));
    setPdfProgress({ current: 0, total: 0, message: "" });

    const formData = new FormData();
    formData.append("file", pdfFile);
    formData.append("target_language", langName);
    formData.append("pages", selectedPages);
    formData.append("output_type", outputType);

    try {
      if (outputType === "text") {
        const res = await axios.post(`${API}/translate_pdf_text`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
        setTranslated(res.data.translated_text);
        showToast(t.toastPdfText);
      } else if (outputType === "html") {
        const res = await axios.post(`${API}/translate_pdf_html`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
        const htmlContent = res.data.html;
        const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `traducido_${pdfFile.name.replace(/\.pdf$/i, "")}.html`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        showToast(t.toastPdfHtml);
      } else {
        const startRes = await axios.post(`${API}/translate_pdf_with_progress`, formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 });
        const jobId = startRes.data.job_id;

        const pollProgress = async () => {
          try {
            const progressRes = await axios.get(`${API}/translation_progress/${jobId}`);
            const progress = progressRes.data;
            setPdfProgress({ current: progress.current_page, total: progress.pages_to_translate, message: progress.message });

            if (progress.status === "completed") {
              clearInterval(progressIntervalRef.current);
              const resultRes = await axios.get(`${API}/translation_result/${jobId}`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([resultRes.data]));
              const link = document.createElement("a");
              link.href = url;
              link.setAttribute("download", `traducido_${pdfFile.name}`);
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
              showToast(t.toastPdfDone);
              stopTimer(); setIsTranslating(false);
              setPdfProgress({ current: 0, total: 0, message: "" });
            } else if (progress.status === "error") {
              clearInterval(progressIntervalRef.current);
              showToast(progress.error || t.errPdfTranslate, "error");
              stopTimer(); setIsTranslating(false);
              setPdfProgress({ current: 0, total: 0, message: "" });
            }
          } catch (err) { console.error(err); }
        };
        progressIntervalRef.current = setInterval(pollProgress, 1500);
        pollProgress();
        return;
      }
    } catch (error) {
      showToast(error.code === "ECONNABORTED" ? t.errTimeout : t.errPdfTranslate, "error");
    }
    stopTimer(); setIsTranslating(false);
    setPdfProgress({ current: 0, total: 0, message: "" });
  };

  const handleSave = () => {
    if (!saveName.trim()) { showToast(t.errName, "error"); return; }
    setHistory((prev) => [{ id: Date.now(), name: saveName, original: pattern, translated, lang: LANGUAGES.find((l) => l.code === targetLang)?.name, date: new Date().toLocaleDateString() }, ...prev]);
    setShowSaveModal(false); setSaveName(""); showToast(t.toastSaved);
  };

  // ── Bug report handler ─────────────────────────────────────────────────────
  const handleSendReport = async () => {
    if (!reportDesc.trim()) { showToast(t.reportDescRequired, "error"); return; }
    setIsSendingReport(true);
    try {
      await loadEmailJS();

      let screenshotData = "No se adjuntó captura de pantalla.";
      if (reportScreenshot) {
        // EmailJS doesn't support file attachments on free plan,
        // so we include a note that screenshot was provided
        screenshotData = `Captura adjunta: ${reportScreenshotName} (${(reportScreenshot.size / 1024).toFixed(1)} KB)`;
      }

      const templateParams = {
        to_email: "crochetranslator@gmail.com",
        filename: pdfFile ? pdfFile.name : "Sin archivo (traducción de texto)",
        browser_info: `${navigator.userAgent}`,
        page_url: window.location.href,
        date: new Date().toLocaleString(),
        description: reportDesc,
        screenshot_info: screenshotData,
      };

      await window.emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams
      );

      showToast(t.reportSuccess);
      setShowReportModal(false);
      setReportDesc("");
      setReportScreenshot(null);
      setReportScreenshotName("");
    } catch (err) {
      console.error("EmailJS error:", err);
      showToast(t.reportError, "error");
    } finally {
      setIsSendingReport(false);
    }
  };

  const progressPct = pdfProgress.total > 0 ? (pdfProgress.current / pdfProgress.total) * 100 : 0;

  const s = {
    app: { minHeight: "100vh", background: "linear-gradient(135deg, #FDF0F5 0%, #FAE6EF 30%, #F5EAF7 60%, #FDE8F0 100%)", fontFamily: "Georgia, serif", position: "relative", overflow: "hidden" },
    container: { maxWidth: 1200, margin: "0 auto", padding: "40px 24px", position: "relative", zIndex: 1 },
    title: { fontSize: "clamp(1.8rem,5vw,3.2rem)", color: "#C06080", fontWeight: "bold", textAlign: "center", marginBottom: 8, textShadow: "2px 2px 8px rgba(220,150,180,0.3)" },
    subtitle: { fontStyle: "italic", color: "#B08898", textAlign: "center", marginBottom: 32 },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(380px,1fr))", gap: 24, marginBottom: 24 },
    card: { background: "rgba(255,252,254,0.92)", borderRadius: 16, padding: 28, border: "1px solid rgba(220,170,195,0.4)", boxShadow: "4px 4px 0px #F0C8D8", position: "relative", overflow: "hidden" },
    accent: { position: "absolute", top: 0, left: 0, right: 0, height: 3, background: "linear-gradient(90deg,#D4829F,#C07B9F,#E8A0C0,#D4829F)" },
    cardTitle: { fontSize: "1.25rem", color: "#A05575", margin: "0 0 14px 0" },
    textarea: { width: "100%", height: 280, border: "2px solid #F0D0DF", borderRadius: 10, padding: 14, fontSize: "0.95rem", fontFamily: "Courier New, monospace", lineHeight: 1.8, resize: "none", outline: "none", background: "#FFFAFC", boxSizing: "border-box" },
    row: { display: "flex", alignItems: "center", justifyContent: "center", flexWrap: "wrap", gap: 14, marginTop: 20 },
    btn: (bg = "#C07B9F", sh = "#9A5F7F") => ({ padding: "11px 26px", background: bg, color: "white", border: "none", borderRadius: 10, fontSize: "1rem", fontFamily: "Georgia,serif", cursor: "pointer", boxShadow: `3px 3px 0px ${sh}`, display: "flex", alignItems: "center", gap: 8 }),
    outBtn: (c = "#C07B9F") => ({ padding: "10px 18px", background: "transparent", color: c, border: `2px dashed ${c}`, borderRadius: 10, fontSize: "0.95rem", fontFamily: "Georgia,serif", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }),
    select: { padding: "10px 14px", border: "2px solid #F0D0DF", borderRadius: 10, fontSize: "1rem", fontFamily: "Georgia,serif", color: "#6A4A5A", background: "white", outline: "none", minWidth: 170 },
    input: { padding: "9px 14px", border: "2px solid #F0D0DF", borderRadius: 8, fontSize: "0.95rem", fontFamily: "Georgia,serif", outline: "none" },
    toast: (type) => ({ position: "fixed", top: 24, left: "50%", transform: "translateX(-50%)", padding: "11px 22px", borderRadius: 12, color: "white", background: type === "error" ? "#E05C5C" : "#C07B9F", zIndex: 9999, fontFamily: "Georgia,serif", boxShadow: "0 4px 16px rgba(0,0,0,0.15)", whiteSpace: "nowrap" }),
    overlay: { position: "fixed", inset: 0, background: "rgba(180,100,130,0.2)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" },
    modal: { background: "white", borderRadius: 16, padding: 30, width: "90%", maxWidth: 420, boxShadow: "4px 4px 0px #F0C8D8" },
    modalInput: { width: "100%", padding: "10px 14px", border: "2px solid #F0D0DF", borderRadius: 8, fontSize: "1rem", fontFamily: "Georgia,serif", outline: "none", boxSizing: "border-box" },
    progressBar: { width: "100%", background: "#FAE6EF", borderRadius: 20, height: 10, overflow: "hidden", marginTop: 8 },
    progressFill: (pct) => ({ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#E8A0C0,#C07B9F)", borderRadius: 20, transition: "width 0.5s ease" }),
  };

  const decos = [
    { emoji: "🧶", x: "-10px", y: "-10px", size: "130px", opacity: 0.4,  rotate: -10, grey: false },
    { emoji: "🧶", x: "80%",   y: "-15px", size: "110px", opacity: 0.35, rotate: 15,  grey: false },
    { emoji: "🧶", x: "-15px", y: "55%",   size: "100px", opacity: 0.3,  rotate: 20,  grey: false },
    { emoji: "🧶", x: "83%",   y: "60%",   size: "120px", opacity: 0.38, rotate: -15, grey: false },
    { emoji: "🧶", x: "40%",   y: "82%",   size: "90px",  opacity: 0.25, rotate: 5,   grey: false },
    { emoji: "🌸", x: "60%",   y: "-5px",  size: "80px",  opacity: 0.45, rotate: 10,  grey: false },
    { emoji: "🌸", x: "5%",    y: "22%",   size: "70px",  opacity: 0.4,  rotate: -8,  grey: false },
    { emoji: "🌸", x: "88%",   y: "30%",   size: "75px",  opacity: 0.38, rotate: 12,  grey: false },
    { emoji: "🌸", x: "18%",   y: "78%",   size: "65px",  opacity: 0.35, rotate: -5,  grey: false },
    { emoji: "🌺", x: "70%",   y: "72%",   size: "70px",  opacity: 0.38, rotate: 8,   grey: false },
    { emoji: "🌹", x: "25%",   y: "-5px",  size: "60px",  opacity: 0.35, rotate: -12, grey: false },
    { emoji: "🪡", x: "90%",   y: "12%",   size: "55px",  opacity: 0.3,  rotate: 35,  grey: false },
    { emoji: "🪡", x: "4%",    y: "42%",   size: "50px",  opacity: 0.28, rotate: -25, grey: false },
    { emoji: "🌿", x: "50%",   y: "88%",   size: "60px",  opacity: 0.3,  rotate: 15,  grey: false },
    { emoji: "🌿", x: "75%",   y: "45%",   size: "50px",  opacity: 0.25, rotate: -10, grey: false },
    { emoji: "🐱", x: "92%",   y: "55%",   size: "55px",  opacity: 0.3,  rotate: 10,  grey: true },
    { emoji: "😺", x: "8%",    y: "68%",   size: "50px",  opacity: 0.28, rotate: -8,  grey: true },
    { emoji: "🐾", x: "32%",   y: "5%",    size: "45px",  opacity: 0.25, rotate: 15,  grey: true },
    { emoji: "😸", x: "55%",   y: "70%",   size: "50px",  opacity: 0.27, rotate: -12, grey: true },
    { emoji: "🐱", x: "15%",   y: "10%",   size: "48px",  opacity: 0.25, rotate: 5,   grey: true },
  ];

  return (
    <div style={s.app}>
      {decos.map((d, i) => (
        <div key={i} style={{ position: "fixed", left: d.x, top: d.y, fontSize: d.size, opacity: d.opacity, pointerEvents: "none", zIndex: 0, transform: `rotate(${d.rotate}deg)`, userSelect: "none", lineHeight: 1, filter: d.grey ? "grayscale(1) brightness(0.75)" : "none" }}>
          {d.emoji}
        </div>
      ))}

      <AnimatePresence>
        {toast && <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} style={s.toast(toast.type)}>{toast.msg}</motion.div>}
      </AnimatePresence>

      <div style={s.container}>
        {/* UI Language selector */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16, alignItems: "center", gap: 10 }}>
          <span style={{ fontStyle: "italic", color: "#B08898", fontSize: "0.9rem" }}>🌐</span>
          <select value={uiLang} onChange={e => setUiLang(e.target.value)}
            style={{ padding: "8px 14px", border: "2px solid #F0D0DF", borderRadius: 10, fontSize: "0.95rem", fontFamily: "Georgia,serif", color: "#6A4A5A", background: "white", outline: "none", cursor: "pointer" }}>
            <option value="es">🇪🇸 Español</option>
            <option value="en">🇬🇧 English</option>
            <option value="fr">🇫🇷 Français</option>
            <option value="pt">🇵🇹 Português</option>
            <option value="de">🇩🇪 Deutsch</option>
            <option value="it">🇮🇹 Italiano</option>
            <option value="ru">🇷🇺 Русский</option>
          </select>
        </div>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 style={s.title}>🧶 {t.title}</h1>
          <p style={s.subtitle}>{t.subtitle}</p>
        </motion.div>

        {/* Main grid */}
        <div style={s.grid}>
          <div style={s.card}>
            <div style={s.accent} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h2 style={s.cardTitle}>📝 {t.originalPattern}</h2>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ ...s.outBtn("#A07080"), padding: "5px 12px", fontSize: "0.85rem" }} onClick={() => setExpandModal({ title: `📝 ${t.originalPattern}`, content: pattern, readOnly: false, onChange: setPattern })} title="Expandir">⛶</button>
                {pattern && <button style={{ ...s.outBtn("#C07B9F"), padding: "5px 12px", fontSize: "0.85rem" }} onClick={() => setPattern("")}>{t.clean}</button>}
                <button style={{ ...s.outBtn("#6A8C6A"), padding: "5px 12px", fontSize: "0.85rem" }} onClick={() => fileInputRef.current?.click()} disabled={isUploadingPdf}>
                  {isUploadingPdf ? `⏳ ${t.loadingPdf}` : `📎 ${t.uploadPdf}`}
                </button>
                <input ref={fileInputRef} type="file" accept=".pdf" onChange={handlePdfUpload} style={{ display: "none" }} />
              </div>
            </div>
            <textarea style={s.textarea} value={pattern} onChange={e => setPattern(e.target.value)} placeholder={t.pasteHere} />
          </div>

          <div style={s.card}>
            <div style={{ ...s.accent, background: "linear-gradient(90deg,#C07B9F,#6A8C6A,#C07B9F)" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h2 style={s.cardTitle}>✨ {t.translatedPattern}</h2>
              <div style={{ display: "flex", gap: 8 }}>
                <button style={{ ...s.outBtn("#A07080"), padding: "5px 12px", fontSize: "0.85rem" }} onClick={() => setExpandModal({ title: `✨ ${t.translatedPattern}`, content: translated || "", readOnly: true })} title="Expandir">⛶</button>
                {translated && <><button style={{ ...s.outBtn("#6A8C6A"), padding: "5px 12px", fontSize: "0.85rem" }} onClick={() => { navigator.clipboard.writeText(translated); showToast(t.copied); }}>{t.copy}</button>
                <button style={{ ...s.outBtn("#C07B9F"), padding: "5px 12px", fontSize: "0.85rem" }} onClick={() => setTranslated("")}>{t.clean}</button></>}
              </div>
            </div>
            {translated
              ? <textarea style={{ ...s.textarea, background: "#F5FBF5", borderColor: "#C4DCC4" }} value={translated} readOnly />
              : <div style={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", background: "#F5FBF5", borderRadius: 10, border: "2px solid #D4E5D4" }}>
                <p style={{ fontStyle: "italic", color: "#C4DCC4" }}>{t.appearsHere}</p>
              </div>
            }
          </div>
        </div>

        {/* Actions */}
        <div style={s.row}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ fontStyle: "italic", color: "#7A5570" }}>{t.translateTo}</label>
            <select style={s.select} value={targetLang} onChange={e => setTargetLang(e.target.value)}>
              {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          <button style={{ ...s.btn(), opacity: isTranslating || !pattern.trim() ? 0.6 : 1, minWidth: 160, justifyContent: "center" }} onClick={handleTranslate} disabled={isTranslating || !pattern.trim()}>
            {isTranslating && !showPdfOptions ? `⏳ ${timer > 0 ? formatTime(timer) : t.translating}` : `🧶 ${t.translateBtn}`}
          </button>
          {translated && <button style={s.outBtn("#6A8C6A")} onClick={() => setShowSaveModal(true)}>💾 {t.save}</button>}
          <button style={s.outBtn("#6A8C6A")} onClick={() => setShowHistory(!showHistory)}>📜 {t.history} ({history.length})</button>
        </div>

        {/* PDF Options */}
        {showPdfOptions && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ ...s.card, marginTop: 20 }}>
            <div style={s.accent} />
            <h2 style={{ ...s.cardTitle, marginBottom: 16 }}>📄 {t.pdfOptions} — {pdfFile?.name}</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "flex-end" }}>
              <div>
                <p style={{ margin: "0 0 8px", fontStyle: "italic", color: "#7A5570", fontSize: "0.9rem" }}>{t.outputType}</p>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "Georgia,serif", color: "#333" }}>
                    <input type="radio" name="outputType" value="pdf" checked={outputType === "pdf"} onChange={() => setOutputType("pdf")} />
                    {t.outputPdf}
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "Georgia,serif", color: "#333" }}>
                    <input type="radio" name="outputType" value="html" checked={outputType === "html"} onChange={() => setOutputType("html")} />
                    {t.outputHtml}
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontFamily: "Georgia,serif", color: "#333" }}>
                    <input type="radio" name="outputType" value="text" checked={outputType === "text"} onChange={() => setOutputType("text")} />
                    {t.outputText}
                  </label>
                </div>
              </div>
              <div>
                <p style={{ margin: "0 0 8px", fontStyle: "italic", color: "#7A5570", fontSize: "0.9rem" }}>
                  {t.pagesLabel} {pdfPageCount > 0 && <span style={{ color: "#aaa" }}>({t.total} {pdfPageCount})</span>}
                </p>
                <input style={{ ...s.input, minWidth: 180 }} value={selectedPages} onChange={e => setSelectedPages(e.target.value)} placeholder="all, 1-3, 1,3,5" />
              </div>
              <button style={{ ...s.btn("#C07B9F", "#9A5F7F"), opacity: isTranslating ? 0.6 : 1, minWidth: 220, justifyContent: "center" }} onClick={handleTranslatePdf} disabled={isTranslating}>
                {isTranslating ? `⏳ ${timer > 0 ? formatTime(timer) : t.processing}` : outputType === "pdf" ? `⬇️ ${t.downloadPdf}` : outputType === "html" ? `🌐 ${t.downloadHtml}` : `📝 ${t.translateToText}`}
              </button>
              <button style={{ ...s.outBtn("#aaa"), padding: "9px 16px", fontSize: "0.85rem" }} onClick={() => { setShowPdfOptions(false); setPdfFile(null); }}>
                ✕ {t.removePdf}
              </button>
            </div>
            {isTranslating && pdfProgress.total > 0 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ marginTop: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span style={{ fontSize: "0.85rem", color: "#6A8C6A", fontStyle: "italic" }}>{pdfProgress.message}</span>
                  <span style={{ fontSize: "0.85rem", color: "#C07B9F" }}>{pdfProgress.current}/{pdfProgress.total} {t.pages}</span>
                </div>
                <div style={s.progressBar}><div style={s.progressFill(progressPct)} /></div>
              </motion.div>
            )}
            <p style={{ margin: "12px 0 0", fontSize: "0.82rem", fontStyle: "italic", color: "#aaa" }}>{t.pagesHint}</p>
          </motion.div>
        )}

        {/* History */}
        {showHistory && (
          <div style={{ ...s.card, marginTop: 20, padding: 20 }}>
            <div style={s.accent} />
            <h3 style={{ ...s.cardTitle, marginBottom: 12 }}>📜 {t.history}</h3>
            {history.length === 0
              ? <p style={{ fontStyle: "italic", color: "#aaa", textAlign: "center", padding: "20px 0" }}>{t.noHistory}</p>
              : history.map(e => (
                <div key={e.id} style={{ padding: "10px 14px", border: "1px solid #D4E5D4", borderRadius: 10, marginBottom: 8, background: "#FAFEFA", cursor: "pointer", display: "flex", justifyContent: "space-between" }}
                  onClick={() => { setPattern(e.original); setTranslated(e.translated); setShowHistory(false); showToast(`${t.toastLoaded} ${e.name}`); }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: "bold", color: "#557A55" }}>{e.name}</p>
                    <p style={{ margin: "3px 0 0", fontSize: "0.82rem", color: "#888" }}>{e.lang} · {e.date}</p>
                  </div>
                  <button style={{ background: "none", border: "none", color: "#e53e3e", cursor: "pointer", fontSize: "1rem" }}
                    onClick={ev => { ev.stopPropagation(); setHistory(prev => prev.filter(x => x.id !== e.id)); showToast(t.toastDeleted); }}>🗑</button>
                </div>
              ))
            }
          </div>
        )}

        {/* Explain row */}
        {translated && (
          <div style={{ ...s.card, marginTop: 20 }}>
            <div style={s.accent} />
            <h2 style={{ ...s.cardTitle, marginBottom: 8 }}>💡 {t.explainTitle}</h2>
            <p style={{ fontStyle: "italic", color: "#888", marginBottom: 12, fontSize: "0.9rem" }}>{t.explainSubtitle}</p>
            <textarea style={{ ...s.textarea, height: 90, marginBottom: 12 }} value={selectedRow} onChange={e => setSelectedRow(e.target.value)} placeholder={t.rowPlaceholder} />
            <button style={{ ...s.btn("#7BA77D", "#6B9B6B"), opacity: isExplaining || !selectedRow.trim() ? 0.6 : 1 }} onClick={handleExplain} disabled={isExplaining || !selectedRow.trim()}>
              {isExplaining ? `⏳ ${t.explaining}` : `💡 ${t.explainBtn}`}
            </button>
            {explanation && <div style={{ marginTop: 14, padding: 18, background: "#F5FBF5", border: "2px solid #D4E5D4", borderRadius: 12, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
              <strong style={{ color: "#7A5570" }}>{t.explanationLabel}</strong><br />{explanation}
            </div>}
          </div>
        )}

        <p style={{ fontStyle: "italic", color: "#aaa", textAlign: "center", marginTop: 32, fontSize: "0.85rem" }}>{t.footer}</p>
      </div>

      {/* ── Bug Report Button (fixed bottom-right) ───────────────────────── */}
      <button
        onClick={() => setShowReportModal(true)}
        title={t.reportBtn}
        style={{
          position: "fixed", bottom: 24, right: 24, zIndex: 500,
          background: "#C07B9F", color: "white", border: "none",
          borderRadius: 50, width: 52, height: 52, fontSize: "1.4rem",
          cursor: "pointer", boxShadow: "3px 3px 0px #9A5F7F",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "transform 0.15s",
        }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.1)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
      >
        🐛
      </button>

      {/* ── Bug Report Modal ─────────────────────────────────────────────── */}
      {showReportModal && (
        <div style={{ ...s.overlay, zIndex: 600 }} onClick={() => setShowReportModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{ ...s.modal, maxWidth: 500, maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <h2 style={{ ...s.cardTitle, margin: 0 }}>🐛 {t.reportTitle}</h2>
              <button onClick={() => setShowReportModal(false)} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "#aaa", lineHeight: 1 }}>✕</button>
            </div>
            <p style={{ fontStyle: "italic", color: "#888", fontSize: "0.9rem", marginBottom: 20 }}>{t.reportSubtitle}</p>

            {/* File info (auto-filled) */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontStyle: "italic", color: "#7A5570", marginBottom: 6, fontSize: "0.9rem" }}>{t.reportFile}</label>
              <div style={{ padding: "9px 14px", background: "#FAF0F5", border: "2px solid #F0D0DF", borderRadius: 8, fontSize: "0.9rem", color: "#6A4A5A" }}>
                {pdfFile ? `📄 ${pdfFile.name}` : "—"}
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontStyle: "italic", color: "#7A5570", marginBottom: 6, fontSize: "0.9rem" }}>{t.reportDesc} *</label>
              <textarea
                style={{ ...s.modalInput, height: 120, resize: "vertical", fontFamily: "Georgia,serif", fontSize: "0.95rem", lineHeight: 1.6 }}
                value={reportDesc}
                onChange={e => setReportDesc(e.target.value)}
                placeholder={t.reportDescPlaceholder}
                autoFocus
              />
            </div>

            {/* Screenshot upload */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "block", fontStyle: "italic", color: "#7A5570", marginBottom: 6, fontSize: "0.9rem" }}>{t.reportScreenshot}</label>
              <input
                ref={screenshotInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) { setReportScreenshot(f); setReportScreenshotName(f.name); }
                }}
              />
              <button
                style={{ ...s.outBtn("#C07B9F"), fontSize: "0.9rem", padding: "8px 16px" }}
                onClick={() => screenshotInputRef.current?.click()}
              >
                📸 {reportScreenshotName || "Seleccionar imagen"}
              </button>
              {reportScreenshotName && (
                <span style={{ marginLeft: 10, fontSize: "0.85rem", color: "#6A8C6A" }}>
                  ✓ {reportScreenshotName}
                  <button onClick={() => { setReportScreenshot(null); setReportScreenshotName(""); }} style={{ background: "none", border: "none", color: "#e53e3e", cursor: "pointer", marginLeft: 6, fontSize: "0.85rem" }}>✕</button>
                </span>
              )}
            </div>

            {/* Technical info (shown to user so they know what's sent) */}
            <div style={{ padding: "10px 14px", background: "#FAF8FF", border: "1px solid #E8D0F0", borderRadius: 8, marginBottom: 20, fontSize: "0.8rem", color: "#888" }}>
              🌐 {window.location.href}<br />
              💻 {navigator.userAgent.split(")")[0].split("(")[1] || navigator.userAgent.substring(0, 60)}
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button style={{ ...s.outBtn("#888"), flex: 1, justifyContent: "center" }} onClick={() => setShowReportModal(false)}>{t.cancel}</button>
              <button
                style={{ ...s.btn(), flex: 1, justifyContent: "center", opacity: isSendingReport || !reportDesc.trim() ? 0.6 : 1 }}
                onClick={handleSendReport}
                disabled={isSendingReport || !reportDesc.trim()}
              >
                {isSendingReport ? `⏳ ${t.reportSending}` : `📨 ${t.reportSend}`}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Expand Modal */}
      {expandModal && (
        <div style={s.overlay} onClick={() => setExpandModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            style={{ background: "white", borderRadius: 16, padding: 28, width: "95vw", maxWidth: 900, maxHeight: "90vh", boxShadow: "4px 4px 0px #F0C8D8", display: "flex", flexDirection: "column", gap: 14 }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ ...s.cardTitle, margin: 0 }}>{expandModal.title}</h2>
              <div style={{ display: "flex", gap: 8 }}>
                {!expandModal.readOnly || expandModal.content ? (
                  <button style={{ ...s.outBtn("#6A8C6A"), padding: "5px 12px", fontSize: "0.85rem" }}
                    onClick={() => { navigator.clipboard.writeText(expandModal.content); showToast(t.copied); }}>
                    {t.copy}
                  </button>
                ) : null}
                <button style={{ ...s.outBtn("#C07B9F"), padding: "5px 14px", fontSize: "1rem", fontWeight: "bold" }} onClick={() => setExpandModal(null)}>✕</button>
              </div>
            </div>
            <textarea
              style={{ ...s.textarea, height: "70vh", resize: "none", fontSize: "0.95rem" }}
              value={expandModal.content}
              readOnly={expandModal.readOnly}
              onChange={expandModal.readOnly ? undefined : e => {
                setExpandModal(prev => ({ ...prev, content: e.target.value }));
                if (expandModal.onChange) expandModal.onChange(e.target.value);
              }}
              placeholder={expandModal.readOnly ? t.appearsHere : t.pasteHere}
            />
          </motion.div>
        </div>
      )}

      {/* Save Modal */}
      {showSaveModal && (
        <div style={s.overlay} onClick={() => setShowSaveModal(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h2 style={{ ...s.cardTitle, marginBottom: 16 }}>💾 {t.saveModal}</h2>
            <label style={{ fontStyle: "italic", color: "#7A5570", display: "block", marginBottom: 8 }}>{t.saveLabel}</label>
            <input style={s.modalInput} value={saveName} onChange={e => setSaveName(e.target.value)} placeholder={t.savePlaceholder} onKeyDown={e => e.key === "Enter" && handleSave()} autoFocus />
            <div style={{ display: "flex", gap: 12, marginTop: 18 }}>
              <button style={{ ...s.outBtn("#888"), flex: 1, justifyContent: "center" }} onClick={() => setShowSaveModal(false)}>{t.cancel}</button>
              <button style={{ ...s.btn(), flex: 1, justifyContent: "center", opacity: !saveName.trim() ? 0.6 : 1 }} onClick={handleSave} disabled={!saveName.trim()}>{t.saveBtn}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
